import { z } from "zod";
import {
  type BackgroundJobType,
  ChordLaneSchema,
  ChordsSchema,
  DefaultLayoutSchema,
  type FontInfo,
  LayoutSchema,
  LineSchema,
  PitchedAudioSchema,
  type TextStyle,
  TextStyleSchema,
  TranslationDataSchema,
} from "./types";

export const VIDEO_WIDTH = 1920;
export const VIDEO_HEIGHT = 1080;
export const VIDEO_FPS = 30;
export const KARAOKE_COMPOSITION_NAME = "KaraokeVideo" as const;
export const VIDEO_EDITOR_COMPOSITION_NAME = "VideoEditor" as const;

export const defaultTextStyle: TextStyle = TextStyleSchema.parse({});

export const AudioVolumeSlotKeySchema = z.enum([
  "original_url",
  "instrumental_url",
  "vocals_url",
  "vocals_backing_url",
  "vocals_lead_url",
]);
export type AudioVolumeSlotKey = z.infer<typeof AudioVolumeSlotKeySchema>;

export const VolumeKeyframeSchema = z.object({
  frame: z.number().int().nonnegative(),
  volume: z.number().int().min(0).max(100),
});
export type VolumeKeyframe = z.infer<typeof VolumeKeyframeSchema>;

export const AudioVolumeAutomationSchema = z.object({
  original_url: z.array(VolumeKeyframeSchema).optional(),
  instrumental_url: z.array(VolumeKeyframeSchema).optional(),
  vocals_url: z.array(VolumeKeyframeSchema).optional(),
  vocals_backing_url: z.array(VolumeKeyframeSchema).optional(),
  vocals_lead_url: z.array(VolumeKeyframeSchema).optional(),
});
export type AudioVolumeAutomation = z.infer<typeof AudioVolumeAutomationSchema>;

const KaraokeCompSchema = z.object({
  /* Audio */
  audioSrc: z.string().nullable(),
  audioMuted: z.boolean().optional(),
  vocalAudioSrc: z.string().optional(),
  vocalAudioVolume: z.number().nonnegative().default(0),
  vocalBackingSrc: z.string().nullish(),
  vocalBackingVolume: z.number().nonnegative().default(100),
  vocalLeadSrc: z.string().nullish(),
  vocalLeadVolume: z.number().nonnegative().default(0),
  instrumentalVolume: z.number().nonnegative().default(100),

  /* Audio Fade */
  audioFadeInSeconds: z.number().nonnegative().max(10).default(0),
  audioFadeOutSeconds: z.number().nonnegative().max(10).default(0),
  audioVolumeAutomation: AudioVolumeAutomationSchema.optional(),

  startFrame: z.number().int().nonnegative().optional(),
  endFrame: z.number().int().positive().optional(),
  durationInFrames: z.number().int().positive(),
  lines: z.array(LineSchema),

  /**
   * Array of text styles for different voice purposes.
   * Each index has a specific purpose:
   *
   * - textStyles[0]: Voice 1 / Singer 1 / Primary voice style
   *   Used in all layouts for the primary/default voice
   *
   * - textStyles[1]: Voice 2 / Singer 2 / Secondary voice style
   *   Used when isDuetMode is enabled (across all layouts)
   *
   * - textStyles[2]: Both voices combined style
   *   Used when isDuetMode is enabled for lines where both voices sing together
   *   In Duet layout specifically, also requires bothSingers to be enabled
   *
   * Note: Translation text style is now stored in the DualLanguageLayout's
   * translationStyle property, not in this array.
   */
  textStyles: z
    .array(TextStyleSchema)
    .default([defaultTextStyle, defaultTextStyle, defaultTextStyle]),

  translationData: TranslationDataSchema.optional(),

  bgColor: z.string().optional(),

  isSyncing: z.boolean().optional(),

  watermarked: z.boolean().optional(),

  layout: LayoutSchema,

  /* Chord Lane. Absent means no lane, and the composition renders as before. */
  chords: ChordsSchema.optional(),
  chordLane: ChordLaneSchema.optional(),
  pitchedAudio: PitchedAudioSchema.optional(),

  showBarOnBreaksLongerThan: z.number().int().nullable(),
  showCountdownOnBreaksLongerThan: z.number().int().nullable(),
  showLeadIn: z.boolean(),
  showLeadInOnBreaksLongerThan: z.number().int().nonnegative().default(0),

  bgVideoDurationInFrames: z.number().int().positive().optional(),

  width: z.number().optional().default(VIDEO_WIDTH),
  height: z.number().optional().default(VIDEO_HEIGHT),

  fontsToLoad: z.array(z.custom<FontInfo>()).optional(),

  // Player only
  onError: z
    .function({ input: z.tuple([z.instanceof(Error)]), output: z.void() })
    .optional(),
});
export type KaraokeCompProps = z.infer<typeof KaraokeCompSchema>;

const KaraokeCompRenderSchema = KaraokeCompSchema.omit({
  onError: true,
});
export type KaraokeCompPropsRender = z.infer<typeof KaraokeCompRenderSchema>;

export const defaultCompositionProps = KaraokeCompSchema.parse({
  audioSrc: "",
  lines: [
    {
      id: 0,
      words: [
        {
          id: 0,
          text: "MyKaraoke",
          timestamp: 1,
          duration: 0.5,
        },
        {
          id: 1,
          text: "Video",
          timestamp: 1.5,
          duration: 0.5,
        },
        {
          id: 2,
          text: "is",
          timestamp: 2,
          duration: 0.5,
        },
        {
          id: 3,
          text: "awesome!",
          timestamp: 2.5,
          duration: 2,
        },
      ],
    },
  ],
  audioMuted: false,
  textStyles: [defaultTextStyle, defaultTextStyle],
  durationInFrames: 10 * VIDEO_FPS,
  bgColor: "#000000",
  bgMediaUrl: undefined,
  bgMediaAsCover: false,

  layout: DefaultLayoutSchema.parse({
    type: "default",
    linesPerScreen: 4,
  }),

  isSyncing: false,
  watermarked: false,

  showBarOnBreaksLongerThan: null,
  showCountdownOnBreaksLongerThan: null,
  showLeadIn: false,
  showLeadInOnBreaksLongerThan: 0,

  width: VIDEO_WIDTH,
  height: VIDEO_HEIGHT,

  fontsToLoad: [
    {
      fontFamily: "Roboto",
      importName: "Roboto",
      version: "v30",
      url: "https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900",
      unicodeRanges: {
        "cyrillic-ext":
          "U+0460-052F, U+1C80-1C88, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F",
        cyrillic: "U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116",
        "greek-ext": "U+1F00-1FFF",
        greek:
          "U+0370-0377, U+037A-037F, U+0384-038A, U+038C, U+038E-03A1, U+03A3-03FF",
        vietnamese:
          "U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1EA0-1EF9, U+20AB",
        "latin-ext":
          "U+0100-02AF, U+0304, U+0308, U+0329, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF",
        latin:
          "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
      },
      fonts: {
        italic: {
          "100": {
            "cyrillic-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOiCnqEu92Fr1Mu51QrEz0dL_nz.woff2",
            cyrillic:
              "https://fonts.gstatic.com/s/roboto/v30/KFOiCnqEu92Fr1Mu51QrEzQdL_nz.woff2",
            "greek-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOiCnqEu92Fr1Mu51QrEzwdL_nz.woff2",
            greek:
              "https://fonts.gstatic.com/s/roboto/v30/KFOiCnqEu92Fr1Mu51QrEzMdL_nz.woff2",
            vietnamese:
              "https://fonts.gstatic.com/s/roboto/v30/KFOiCnqEu92Fr1Mu51QrEz8dL_nz.woff2",
            "latin-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOiCnqEu92Fr1Mu51QrEz4dL_nz.woff2",
            latin:
              "https://fonts.gstatic.com/s/roboto/v30/KFOiCnqEu92Fr1Mu51QrEzAdLw.woff2",
          },
          "300": {
            "cyrillic-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TjASc3CsTKlA.woff2",
            cyrillic:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TjASc-CsTKlA.woff2",
            "greek-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TjASc2CsTKlA.woff2",
            greek:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TjASc5CsTKlA.woff2",
            vietnamese:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TjASc1CsTKlA.woff2",
            "latin-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TjASc0CsTKlA.woff2",
            latin:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TjASc6CsQ.woff2",
          },
          "400": {
            "cyrillic-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1Mu51xFIzIFKw.woff2",
            cyrillic:
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1Mu51xMIzIFKw.woff2",
            "greek-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1Mu51xEIzIFKw.woff2",
            greek:
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1Mu51xLIzIFKw.woff2",
            vietnamese:
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1Mu51xHIzIFKw.woff2",
            "latin-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1Mu51xGIzIFKw.woff2",
            latin:
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1Mu51xIIzI.woff2",
          },
          "500": {
            "cyrillic-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51S7ACc3CsTKlA.woff2",
            cyrillic:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51S7ACc-CsTKlA.woff2",
            "greek-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51S7ACc2CsTKlA.woff2",
            greek:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51S7ACc5CsTKlA.woff2",
            vietnamese:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51S7ACc1CsTKlA.woff2",
            "latin-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51S7ACc0CsTKlA.woff2",
            latin:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51S7ACc6CsQ.woff2",
          },
          "700": {
            "cyrillic-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TzBic3CsTKlA.woff2",
            cyrillic:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TzBic-CsTKlA.woff2",
            "greek-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TzBic2CsTKlA.woff2",
            greek:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TzBic5CsTKlA.woff2",
            vietnamese:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TzBic1CsTKlA.woff2",
            "latin-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TzBic0CsTKlA.woff2",
            latin:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TzBic6CsQ.woff2",
          },
          "900": {
            "cyrillic-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TLBCc3CsTKlA.woff2",
            cyrillic:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TLBCc-CsTKlA.woff2",
            "greek-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TLBCc2CsTKlA.woff2",
            greek:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TLBCc5CsTKlA.woff2",
            vietnamese:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TLBCc1CsTKlA.woff2",
            "latin-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TLBCc0CsTKlA.woff2",
            latin:
              "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TLBCc6CsQ.woff2",
          },
        },
        normal: {
          "100": {
            "cyrillic-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1MmgVxFIzIFKw.woff2",
            cyrillic:
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1MmgVxMIzIFKw.woff2",
            "greek-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1MmgVxEIzIFKw.woff2",
            greek:
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1MmgVxLIzIFKw.woff2",
            vietnamese:
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1MmgVxHIzIFKw.woff2",
            "latin-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1MmgVxGIzIFKw.woff2",
            latin:
              "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1MmgVxIIzI.woff2",
          },
          "300": {
            "cyrillic-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmSU5fCRc4EsA.woff2",
            cyrillic:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmSU5fABc4EsA.woff2",
            "greek-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmSU5fCBc4EsA.woff2",
            greek:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmSU5fBxc4EsA.woff2",
            vietnamese:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmSU5fCxc4EsA.woff2",
            "latin-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmSU5fChc4EsA.woff2",
            latin:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmSU5fBBc4.woff2",
          },
          "400": {
            "cyrillic-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu72xKOzY.woff2",
            cyrillic:
              "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu5mxKOzY.woff2",
            "greek-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu7mxKOzY.woff2",
            greek:
              "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4WxKOzY.woff2",
            vietnamese:
              "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu7WxKOzY.woff2",
            "latin-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu7GxKOzY.woff2",
            latin:
              "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2",
          },
          "500": {
            "cyrillic-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmEU9fCRc4EsA.woff2",
            cyrillic:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmEU9fABc4EsA.woff2",
            "greek-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmEU9fCBc4EsA.woff2",
            greek:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmEU9fBxc4EsA.woff2",
            vietnamese:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmEU9fCxc4EsA.woff2",
            "latin-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmEU9fChc4EsA.woff2",
            latin:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmEU9fBBc4.woff2",
          },
          "700": {
            "cyrillic-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfCRc4EsA.woff2",
            cyrillic:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfABc4EsA.woff2",
            "greek-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfCBc4EsA.woff2",
            greek:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfBxc4EsA.woff2",
            vietnamese:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfCxc4EsA.woff2",
            "latin-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfChc4EsA.woff2",
            latin:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfBBc4.woff2",
          },
          "900": {
            "cyrillic-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmYUtfCRc4EsA.woff2",
            cyrillic:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmYUtfABc4EsA.woff2",
            "greek-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmYUtfCBc4EsA.woff2",
            greek:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmYUtfBxc4EsA.woff2",
            vietnamese:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmYUtfCxc4EsA.woff2",
            "latin-ext":
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmYUtfChc4EsA.woff2",
            latin:
              "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmYUtfBBc4.woff2",
          },
        },
      },
    },
  ],
});

// `satisfies`, not a `: BackgroundJobType` annotation: these key
// `JOB_PRICING` directly, so they must keep their own literal type.
export const BASE_MODEL_JOB_TYPE =
  "mykaraoke_lyrics-alignment" satisfies BackgroundJobType;
export const PRO_MODEL_JOB_TYPE =
  "audioshake_lyrics-alignment" satisfies BackgroundJobType;
export const TRANSCRIPTION_JOB_TYPE =
  "audioshake_transcription-alignment" satisfies BackgroundJobType;

export const BASE_VOCAL_SEP_JOB_TYPE =
  "mykaraoke_separate-vocals" satisfies BackgroundJobType;
export const PRO_VOCAL_SEP_JOB_TYPE =
  "lalal_separate-vocals" satisfies BackgroundJobType;

export const BASE_BACKING_VOCAL_SEP_JOB_TYPE: BackgroundJobType =
  "mykaraoke_separate-backing-vocals";
export const PRO_BACKING_VOCAL_SEP_JOB_TYPE: BackgroundJobType =
  "lalal_separate-vocals";

/**
 * Trigger.dev run statuses that mean the run will not complete successfully.
 * Shared by client-side `useRealtimeRun` consumers so they all react to the
 * full set of terminal failures (including `EXPIRED`, which fires when a run
 * exceeds its TTL before executing).
 */
export const TERMINAL_FAILURE_STATUSES = [
  "FAILED",
  "CANCELED",
  "CRASHED",
  "SYSTEM_FAILURE",
  "TIMED_OUT",
  "EXPIRED",
] as const;

export type TerminalFailureStatus = (typeof TERMINAL_FAILURE_STATUSES)[number];
